import React, { useState } from 'react';
import { API_BASE } from '../config';

export default function GpxDebug() {
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // HR Params
    const [restingHr, setRestingHr] = useState('50');
    const [maxHr, setMaxHr] = useState('190');

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setResult(null);
        setError(null);
    };

    const handleUpload = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('gpx', file);
        formData.append('restingHr', restingHr);
        formData.append('maxHr', maxHr);

        try {
            const res = await fetch(`${API_BASE}/upload-gpx`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || 'Upload failed');
            }

            const data = await res.json();
            setResult(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg mt-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">GPX Upload Debug</h1>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Resting HR</label>
                    <input
                        type="number"
                        value={restingHr}
                        onChange={(e) => setRestingHr(e.target.value)}
                        className="w-full border rounded px-3 py-2 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Max HR</label>
                    <input
                        type="number"
                        value={maxHr}
                        onChange={(e) => setMaxHr(e.target.value)}
                        className="w-full border rounded px-3 py-2 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
            </div>

            <div
                className={`mb-6 p-10 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors cursor-pointer ${
                    file ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-blue-400'
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                        setFile(e.dataTransfer.files[0]);
                        setResult(null);
                        setError(null);
                    }
                }}
                onClick={() => document.getElementById('gpx-upload-input').click()}
            >
                <div className="text-4xl mb-3">📂</div>
                <p className="text-gray-600 font-medium mb-1">
                    {file ? file.name : "Click to upload or drag and drop GPX file"}
                </p>
                <p className="text-xs text-gray-400">Supported format: .gpx</p>

                <input
                    id="gpx-upload-input"
                    type="file"
                    accept=".gpx"
                    onChange={handleFileChange}
                    className="hidden"
                />
            </div>

            <div className="flex justify-center mb-6">
                <button
                    onClick={handleUpload}
                    disabled={!file || loading}
                    className={`px-8 py-3 rounded-lg font-bold text-lg shadow-md transition transform active:scale-95 ${
                        !file || loading
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                >
                    {loading ? (
                        <span className="flex items-center gap-2">
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing...
                        </span>
                    ) : 'Upload & Analyze GPX'}
                </button>
            </div>

            {error && <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg text-center font-bold">{error}</div>}

            {result && (
                <div className="space-y-6 animate-fade-in">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-center">
                            <div className="text-sm text-gray-500 uppercase font-bold">Distance</div>
                            <div className="text-2xl font-bold text-blue-900">{result.summary.distance_km} <span className="text-sm font-normal">km</span></div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg border border-green-100 text-center">
                            <div className="text-sm text-gray-500 uppercase font-bold">Duration</div>
                            <div className="text-2xl font-bold text-green-900">
                                {Math.floor(result.summary.duration_seconds / 60)} <span className="text-sm font-normal">min</span>
                            </div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 text-center">
                            <div className="text-sm text-gray-500 uppercase font-bold">Elev Gain</div>
                            <div className="text-2xl font-bold text-orange-900">+{result.summary.elevation_gain_m} <span className="text-sm font-normal">m</span></div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center">
                            <div className="text-sm text-gray-500 uppercase font-bold">Elev Loss</div>
                            <div className="text-2xl font-bold text-red-900">-{result.summary.elevation_loss_m} <span className="text-sm font-normal">m</span></div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 text-center">
                            <div className="text-sm text-gray-500 uppercase font-bold">Avg HR</div>
                            <div className="text-2xl font-bold text-purple-900">{result.summary.avg_hr || '-'} <span className="text-sm font-normal">bpm</span></div>
                        </div>
                    </div>

                    {/* HR Zones */}
                    {Object.keys(result.hr_zones).length > 0 && (
                         <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="font-bold text-gray-800 mb-3">Heart Rate Zones</h3>
                            <div className="space-y-2">
                                {Object.entries(result.hr_zones).map(([zone, count]) => (
                                    <div key={zone} className="flex items-center text-sm">
                                        <div className="w-24 font-medium text-gray-600">{zone}</div>
                                        <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-red-500"
                                                style={{ width: `${(count / (result.summary.duration_seconds || 1)) * 100}%` }} // Rough approx if count is roughly seconds
                                            ></div>
                                            {/* Note: count is number of points, usually 1 per sec but depends on file. */}
                                        </div>
                                        <div className="w-12 text-right text-gray-500">{count} pts</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* KM Splits */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-gray-700">KM Splits</div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">KM</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pace</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Elev +</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Elev -</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg HR</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cadence</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {result.splits.map((split) => (
                                        <tr key={split.km} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 text-sm font-bold text-gray-900">{split.km}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">
                                                {Math.floor(split.time_seconds / 60)}:{(Math.round(split.time_seconds % 60)).toString().padStart(2, '0')}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600">
                                                {/* Pace calculation min/km */}
                                                {Math.floor(split.time_seconds / 60)}'{(Math.round(split.time_seconds % 60)).toString().padStart(2, '0')}"
                                            </td>
                                            <td className="px-4 py-2 text-sm font-bold text-orange-600">+{split.elevation_gain}m</td>
                                            <td className="px-4 py-2 text-sm text-gray-400">-{split.elevation_loss}m</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{split.avg_hr || '-'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{split.avg_cadence || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Raw JSON Debug */}
                     <details className="bg-gray-900 rounded-lg p-4">
                        <summary className="text-gray-400 font-mono text-sm cursor-pointer mb-2">View Raw JSON</summary>
                        <pre className="text-green-400 text-xs overflow-x-auto">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
        </div>
    );
}

